import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { logout } from '../store/user/user.actions';
import { environment } from 'src/environments/environment';

interface StoredTokens {
  _accessToken?: string;
  _refreshToken?: string;
}

function readStoredTokens(key: string): StoredTokens | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    return null;
  }
}

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  constructor(private router: Router, private store: Store) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // TODO (security): JWTs (access + refresh) live in localStorage — any XSS
    // means full account takeover with no client-side revocation. For a trading
    // platform, prefer httpOnly cookies.
    const userData = readStoredTokens('userData');
    const faqsToken = readStoredTokens('faqsAccessToken');

    // exclude wordpress endpoint Authentication
    if (req.url.startsWith(environment.wordpress.endpoint)) {
      return next.handle(req);
    }
    if (userData?._accessToken && userData._refreshToken) {
      const bearer =
        req.body.operationName === 'RefreshToken'
          ? userData._refreshToken
          : userData._accessToken;
      const cloned = req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + bearer),
      });

      return next.handle(cloned).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Handle 401 error - token expired
            // Optionally, refresh the token here
            this.handleAuthError();
          }
          return throwError(() => error);
        })
      );
    } else if (faqsToken?._accessToken) {
      // logic for native mobile apps opens webview that injects access token to localStorage
      const cloned = req.clone({
        headers: req.headers.set(
          'Authorization',
          'Bearer ' + faqsToken._accessToken
        ),
      });

      return next.handle(cloned).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Handle 401 error - token expired
            // Optionally, refresh the token here
            this.handleAuthError();
          }
          return throwError(() => error);
        })
      );
    } else {
      return next.handle(req);
    }
  }

  private handleAuthError(): void {
    localStorage.removeItem('userData');
    localStorage.removeItem('faqsAccessToken'); // clear mobile-injected token too
    this.store.dispatch(logout());
    this.router.navigate(['/login']);
  }
}
